import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { type CondominiumOption, ROLE_LABELS } from '@brimostech/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@brimostech/ui';
import { groupOptionsByOrganization } from './group-options';

export interface CondominiumSwitcherProps {
  options: CondominiumOption[];
  currentId: string | null;
  onSelect: (condominiumId: string) => void;
}

/** Header selector that changes the active condominium for the whole interface. */
export function CondominiumSwitcher({ options, currentId, onSelect }: CondominiumSwitcherProps) {
  const current = options.find((o) => o.id === currentId) ?? null;

  if (options.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Building2 className="size-4" />
        <span>Sem condomínio vinculado</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 max-w-[16rem] gap-2 px-2 font-medium"
          aria-label="Trocar condomínio"
        >
          <Building2 className="size-4 text-muted-foreground" />
          <span className="truncate">{current?.name ?? 'Selecione um condomínio'}</span>
          <ChevronsUpDown className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        {groupOptionsByOrganization(options).map((group, index) => (
          <div key={group.organizationName}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              {group.organizationName}
            </DropdownMenuLabel>
            {group.options.map((option) => (
              <DropdownMenuItem
                key={option.id}
                onSelect={() => {
                  if (option.id !== currentId) onSelect(option.id);
                }}
                className="flex items-center gap-2"
              >
                <span className="flex-1 truncate">{option.name}</span>
                <span className="text-xs text-muted-foreground">{ROLE_LABELS[option.role]}</span>
                {option.id === currentId ? <Check className="size-4 text-primary" /> : null}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
